import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        defaultSoft:
          "border-primary bg-primary/30 text-primary hover:bg-primary/30",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        secondarySoft:
          "border-secondary-foreground/30 bg-secondary/30 text-secondary-foreground hover:bg-secondary/30",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        destructiveSoft:
          "border-destructive bg-destructive/30 text-destructive hover:bg-destructive/30",
        warningSoft:
          "border-warning bg-warning/30 text-warning hover:bg-warning/30 dark:bg-warning/20",
        successSoft:
          "border-success bg-success/10 text-success hover:bg-success/10 dark:bg-success/15",
        promotionSoft:
          "border-promotion bg-promotion/30 text-promotion-foreground hover:bg-promotion/30 dark:bg-promotion/20 dark:text-promotion",
        /** Match quality — Strong. */
        matchStrong:
          "border-success/50 bg-success/20 text-success font-semibold hover:bg-success/20",
        /** Match quality — Good. */
        matchGood:
          "border-primary/40 bg-primary/20 text-primary font-semibold hover:bg-primary/20",
        /** Match quality — Worth a look (neutral). */
        matchMixed:
          "border-border bg-muted/50 text-muted-foreground font-medium hover:bg-muted/50",
        /** Match quality — Limited fit. */
        matchLimited:
          "border-warning/50 bg-warning/20 text-warning font-semibold hover:bg-warning/20 dark:bg-warning/15",
        /** Match quality — Poor fit. */
        matchPoor:
          "border-destructive/40 bg-destructive/15 text-destructive font-semibold hover:bg-destructive/15",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
